/**
 * Slot-backed date and time field for appointment forms, combining conflict alerts with the
 * shared `SlotDateTimePicker` wired through `useAppointmentSlotSelection`.
 */

import React from 'react';
import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import { ConflictAlerts } from '../create-appointment/components/ConflictAlerts';
import { SlotDateTimePicker } from '../create-appointment/components/SlotDateTimePicker';
import { useAppointmentSlotSelection } from './useAppointmentSlotSelection';
import { useServiceAppointmentSlotSelection } from './useServiceAppointmentSlotSelection';
import { appointmentSlotDatePickerLocale } from './appointmentSlotDatePickerLocale';

/** Props for rendering a resource-scoped slot picker inside an appointment form. */
interface AppointmentSlotDateTimeFieldProps {
  /** Form that owns the `startTime` field. */
  form: FormInstance;
  /** Key used to remount the picker when the dialog or appointment context changes. */
  dialogKey: number | string;
  /** Resource whose availability drives disabled dates and times. */
  resourceId: string | null | undefined;
  /** Appointment length in minutes passed through to the picker. */
  duration: number;
  /** Whether the hosting surface is open; controls slot loading in the internal hook. */
  visible: boolean;
  /** Optional day used to open the calendar without an initial committed start time. */
  preferredDate?: Dayjs;
  /** Whether the current `startTime` should count as committed when the field mounts. */
  initialCommitted?: boolean;
  /** Existing interval that remains selectable even if absent from fresh slot results. */
  treatAsValidSlot?: { start: Dayjs; end: Dayjs } | null;
  /** Popup container resolver forwarded to the underlying date picker. */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  label?: React.ReactNode;
  help?: React.ReactNode;
  placeholder?: string;
  size?: 'large' | 'middle' | 'small';
  /** Picker rendering style: classic popover trigger or inline panel. */
  displayMode?: 'popover' | 'inline';
  /** When `false`, hides slot conflict and truncation alerts above the picker. */
  showAlerts?: boolean;
  /** Optional precomputed selection state to share one hook instance across siblings. */
  selection?:
    | ReturnType<typeof useAppointmentSlotSelection>
    | ReturnType<typeof useServiceAppointmentSlotSelection>;
}

/**
 * Renders slot validation alerts and a policy-aware date/time picker for appointment booking flows.
 */
export const AppointmentSlotDateTimeField: React.FC<AppointmentSlotDateTimeFieldProps> = ({
  form,
  dialogKey,
  resourceId,
  duration,
  visible,
  preferredDate,
  initialCommitted,
  treatAsValidSlot,
  getPopupContainer,
  label,
  help,
  placeholder,
  size,
  displayMode = 'popover',
  showAlerts = true,
  selection: externalSelection
}) => {
  const internalSelection = useAppointmentSlotSelection({
    form,
    resourceId,
    duration,
    visible: visible && !externalSelection,
    preferredDate,
    initialCommitted,
    treatAsValidSlot
  });
  const selection = externalSelection ?? internalSelection;

  return (
    <>
      {showAlerts && (
        <ConflictAlerts
          slotUnavailable={selection.displayedSlotUnavailable}
          isCheckingSlots={selection.isCheckingSlots}
          truncated={selection.truncated}
          resourceId={resourceId}
          onDismissSlotUnavailable={() => selection.setDismissedSlotUnavailable(true)}
        />
      )}

      <SlotDateTimePicker
        resourcePolicy={selection.resourcePolicy}
        duration={duration}
        form={form}
        dialogKey={dialogKey}
        useSlotSchedule
        disabledDate={selection.disabledDate}
        disabledTime={selection.disabledTime}
        slotAlignmentMinutes={selection.slotAlignmentMinutes}
        visibleMonth={selection.slotsVisibleMonth}
        onVisibleMonthChange={selection.handleSlotsVisibleMonthChange}
        preferredPickerValue={selection.preferredPickerValue}
        onStartTimeCommit={selection.setHasCommittedStartTime}
        onCalendarDaySelect={selection.handleCalendarDaySelect}
        label={label}
        help={help}
        placeholder={placeholder}
        size={size}
        getPopupContainer={getPopupContainer}
        locale={appointmentSlotDatePickerLocale}
        slotsByDateKey={selection.slotsByDateKey}
        timezone={selection.timezone}
        isLoadingSlots={selection.isLoadingVisibleMonthSlots}
        slotsTruncated={selection.truncated}
        displayMode={displayMode}
      />
    </>
  );
};
